import { observer } from "mobx-react";
import { FC, ReactElement, useEffect, useMemo } from "react";
import {
  ShareholderAdditionalRepresentativesView,
  ShareholderCommitmentsView,
  ShareholderContributionsView,
  ShareholderDetailsLayout,
  ShareholderDetailsLoadingView,
  ShareholderDetailsOverview,
  ShareholderDetailsView,
  ShareholderTransactionsView,
} from "./shareholderDetailsComponents";
import {
  useDispatch,
  useI18n,
  useMessageStore,
  useRootStore,
  useShareholdersStore,
  useTranslation,
  useUserStore,
} from "../../stores/RootStoreContext";
import {
  prepAdditionalRepresentativesTable,
  prepCommitmentsData,
  prepContributionsView,
  prepDetailsCards,
  prepShareholderDetailsActions,
  prepShareholderDetailsDialogs,
  prepShareholderDetailsHeader,
  prepShareholdingAlert,
  prepShareholdingEmptyState,
  prepShareholdingSections,
  prepTransactionsSummaryInfo,
  prepTransactionsTable,
  prepTransactionSummaryCards,
  ShareDetailsDialogsProps,
  ShareholderDetailsActions,
  ShareholderDetailsContext,
  toShareholderDetailsTab,
} from "./shareholderDetailsDomain";
import { InviteShareholdersDialog } from "./ShareholdersPage";
import { Dialog } from "../../components-v2/Dialog";
import {
  adminShareholderDetails,
  adminShareholders,
  adminShareholdersShareholderCommitmentDetails,
  adminShareholdersShareholderCommitments,
  adminShareholdersShareholderContributionDetails,
  adminShareholdersShareholderContributions,
  adminShareholdersTransaction,
  navigationReferrerUrl,
} from "../../utils/navigation";
import { isEmpty } from "lodash";
import AlertMessageStore from "../../stores/AlertMessageStore";
import { ShareholderDetailsState } from "../../stores/ShareholdersStore";
import { ShareholderCommitmentController } from "../commitments/shareholderCommitmentsDomain";
import { ShareholderCommitmentsDialogs } from "../commitments/ShareholderCommitmentsPage";
import { shareholderCommitmentWhitelist } from "../../common/featureToggles";
import { ShareholderPledgeForm, ShareholderRemarksForm } from "../shareholderRegisterDomain";
import { VBox } from "../../components-v2/Box";
import { YesNoQuestion } from "../../dbot/common/commonComponents";
import { TextArea } from "../../components-v2/TextArea";
import { Alert } from "../../components-v2/Alert";
import {
  isContributionsEnabled,
  ShareholderContributionController,
} from "../contributions/shareholderContributionsDomain";
import { ShareholderContributionDialogs } from "../contributions/ShareholderContributionsPage";

export const ShareholderDetailsTabView: FC<{
  context: ShareholderDetailsContext;
  state: ShareholderDetailsState;
  actions: ShareholderDetailsActions;
}> = observer(({ context, state, actions }) => {
  switch (true) {
    case state.isLoading:
      return <ShareholderDetailsLoadingView />;
    case !context.activeTab || context.activeTab === "overview":
      return (
        <ShareholderDetailsOverview
          sections={prepShareholdingSections(state, actions)}
          emptyState={prepShareholdingEmptyState(context, state, actions)}
          alert={prepShareholdingAlert(context, state)}
        />
      );
    case context.activeTab === "transactions":
      return (
        <ShareholderTransactionsView
          transactionsSummaryInfo={prepTransactionsSummaryInfo(state)}
          transactionsSummaryCards={prepTransactionSummaryCards(state)}
          transactionsTable={prepTransactionsTable(context, state, actions)}
        />
      );
    case context.activeTab === "details":
      return <ShareholderDetailsView detailsCards={prepDetailsCards(context, state, actions)} />;
    case context.activeTab === "additional-representatives":
      return (
        <ShareholderAdditionalRepresentativesView
          additionalRepresentativesTable={prepAdditionalRepresentativesTable(state, actions)}
          inviteAdditionalRepresentative={actions.openInviteRepresentativeDialog}
          isEmpty={isEmpty(state.shareholder?.additionalRepresentatives)}
        />
      );
    case context.activeTab === "commitments":
      return <ShareholderCommitmentsView {...prepCommitmentsData(context, state, actions)} />;
    case context.activeTab === "contributions":
      return <ShareholderContributionsView {...prepContributionsView(context, state, actions)} />;
    default:
      return null;
  }
});

export const ShareholderDetailsPageView: FC<{
  alertStore: AlertMessageStore;
  context: ShareholderDetailsContext;
  state: ShareholderDetailsState;
  actions: ShareholderDetailsActions;
  header?: ReactElement;
}> = observer(({ alertStore, context, state, actions, header }) => (
  <ShareholderDetailsLayout
    alertStore={alertStore}
    header={prepShareholderDetailsHeader(context, state, actions)}
    content={
      <>
        <div>{header}</div>
        <ShareholderDetailsTabView context={context} state={state} actions={actions} />
      </>
    }
  />
));

const ShareholderPledgeFormView = observer(({ form }: { form: ShareholderPledgeForm }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <YesNoQuestion question={t("shareholders.are-shares-pledged")} field={form.isPledged} />
      <TextArea
        label={t("shareholders.pledged-shares-comment")}
        value={form.comment.inputValue}
        onChange={form.comment.setInputValue}
        onBlur={form.comment.commit}
      />
      <Alert message={t("shareholders.pledged-shares.info")} type={"info"} />
    </VBox>
  );
});

const ShareholderRemarksFormView = observer(({ form }: { form: ShareholderRemarksForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"}>
      <TextArea
        label={t("shareholders.edit-remarks.remark")}
        value={form.remark.inputValue}
        onChange={form.remark.setInputValue}
        onBlur={form.remark.commit}
      />
      <Alert
        message={t("shareholders.edit-remarks.info", { name: form.shareholderName })}
        type={"info"}
      />
    </VBox>
  );
});

export const ShareholderDetailsDialogs: FC<ShareDetailsDialogsProps> = observer((props) => {
  return (
    <>
      {props.notifyShareholders.show && props.notifyShareholders.store && (
        <InviteShareholdersDialog
          controller={props.notifyShareholders.store}
          onClose={props.notifyShareholders.onClose}
        />
      )}
      {(props.editPledge.form.mode === "edit" || props.editPledge.form.mode === "saving") && (
        <Dialog
          title={{ key: "shareholders.pledged-shares" }}
          onCancel={props.editPledge.onCancel}
          onSubmit={props.editPledge.onSubmit}
          isSubmitting={props.editPledge.form.mode === "saving"}
        >
          <ShareholderPledgeFormView form={props.editPledge.form} />
        </Dialog>
      )}
      {(props.editRemarks.form.mode === "edit" || props.editRemarks.form.mode === "saving") && (
        <Dialog
          title={{
            key: "shareholders.edit-remarks",
            params: { name: props.editRemarks.form.shareholderName },
          }}
          onCancel={props.editRemarks.onCancel}
          onSubmit={props.editRemarks.onSubmit}
          isSubmitDisabled={!props.editRemarks.form.isValid}
          isSubmitting={props.editRemarks.form.mode === "saving"}
        >
          <ShareholderRemarksFormView form={props.editRemarks.form} />
        </Dialog>
      )}
      <ShareholderCommitmentsDialogs controller={props.commitmentCtrl} />
      <ShareholderContributionDialogs controller={props.contributionCtrl} />
    </>
  );
});

export const ShareholderDetailsPage: FC<{
  id: string;
  tab?: string;
  allowCommitments: boolean;
}> = observer((props) => {
  const location = useRootStore().location;
  const queryString = location.search;
  const dispatch = useDispatch();
  const shareholderId = Number(props.id);
  const shareholdersStore = useShareholdersStore();
  const alertStore = useMessageStore();
  const { f, t, lang } = useI18n();
  const userStore = useUserStore();
  const { apiService, http } = useRootStore();
  const orgNo = userStore.orgNumber;
  const canEdit = userStore.userRole?.allowShareholdersEdit ?? true;
  const userRole = userStore.userRole;
  const state = shareholdersStore.getShareholderDetailsState();
  const commitmentCtrl = useMemo(
    () =>
      new ShareholderCommitmentController(
        apiService,
        alertStore,
        orgNo,
        state.shareholder?.commitments,
      ),
    [alertStore, apiService, orgNo, state.shareholder],
  );
  const contributionCtrl = useMemo(
    () =>
      new ShareholderContributionController(
        apiService,
        alertStore,
        orgNo,
        state.shareholder?.shareholderContributions,
      ),
    [apiService, alertStore, orgNo, state.shareholder],
  );
  const commitmentsEnabled =
    props.allowCommitments && shareholderCommitmentWhitelist.includes(orgNo);
  const contributionsEnabled = isContributionsEnabled(orgNo);
  const context: ShareholderDetailsContext = {
    orgNo,
    userRole,
    companyName: shareholdersStore.userStore.companyInfo?.company.name,
    shareholderId,
    activeTab: toShareholderDetailsTab(props.tab),
    commitmentsEnabled,
    commitments: commitmentCtrl.fetchShareholderCommitmentsResult,
    contributionsEnabled,
    contributions: contributionCtrl.fetchShareholderContributionsResult,
    canEdit,
    t,
    f,
    lang,
  };
  const companyName = context.companyName;
  const actions = prepShareholderDetailsActions({
    activeTab: context.activeTab,
    dispatch,
    lang,
    orgNo: context.orgNo,
    setShareholderStatusFilter: shareholdersStore.setShareholderStatusFilter,
    shareholderId,
    referrerUrl:
      navigationReferrerUrl(location.state) ?? adminShareholders(context.orgNo) + queryString,
    shareholderUrl: (orgNo, shareholderId, tab) =>
      adminShareholderDetails(orgNo, shareholderId, tab) + queryString,
    shareholderDetailsStore: shareholdersStore.shareholderDetailsStore,
    commitmentCtrl,
    currentUrl: location.pathname,
    canEdit,
    transactionDetailsUrl: (...args) => adminShareholdersTransaction(...args) + queryString,
    commitmentsUrl: adminShareholdersShareholderCommitments,
    commitmentDetailsUrl: adminShareholdersShareholderCommitmentDetails,
    contributionCtrl,
    contributionsUrl: adminShareholdersShareholderContributions,
    contributionDetailsUrl: adminShareholdersShareholderContributionDetails,
    editDetails: () =>
      shareholdersStore.openEditShareholderDialog({
        locale: lang,
        shareholderId,
        onSuccess: () => {
          shareholdersStore.getShareholderDetails(shareholderId);
          shareholdersStore.loadAllShareholders();
        },
      }),
    deleteShareholder: () =>
      shareholdersStore.deleteShareholder(shareholderId, () =>
        dispatch([
          {
            type: "navigate",
            url:
              navigationReferrerUrl(location.state) ??
              adminShareholders(context.orgNo) + queryString,
          },
        ]),
      ),
    deleteRepresentative: (id) => shareholdersStore.deleteRepresentative(id),
    downloadShareholderCertificate: () =>
      shareholdersStore.downloadShareholderCertificate(shareholderId, lang),
    apiService,
    messageStore: alertStore,
    http,
    user: userStore.user,
    companyName,
  });

  useEffect(() => {
    shareholdersStore.clearMessages();
    shareholdersStore.getShareholderDetails(shareholderId);
    if (shareholdersStore.orgNo !== context.orgNo) {
      shareholdersStore.loadAllShareholders();
    }
    shareholdersStore.getTransactionLog();
    return () => {
      shareholdersStore.shareholderDetailsStore.resetShareholder();
    };
  }, [context.orgNo, shareholderId, shareholdersStore]);

  useEffect(() => {
    if (
      (props.tab === "commitments" && !commitmentsEnabled) ||
      (props.tab === "contributions" && !contributionsEnabled)
    ) {
      dispatch([
        {
          type: "navigate",
          url: adminShareholderDetails(orgNo, shareholderId, "overview") + queryString,
          replace: true,
        },
      ]);
    }
  }, [
    commitmentsEnabled,
    contributionsEnabled,
    dispatch,
    orgNo,
    props.tab,
    queryString,
    shareholderId,
  ]);

  return (
    <>
      <ShareholderDetailsPageView
        alertStore={alertStore}
        context={context}
        state={state}
        actions={actions}
      />
      <ShareholderDetailsDialogs
        {...prepShareholderDetailsDialogs(
          shareholdersStore,
          commitmentCtrl,
          contributionCtrl,
          context,
          state,
        )}
      />
    </>
  );
});
